(function () {
  const storageKey = "roar_selected_phone_model";
  const selector = "[data-global-phone-selector]";
  const triggerSelector = "[data-global-phone-trigger]";
  const labelSelector = "[data-global-phone-label]";
  const optionSelector = "[data-global-phone-option]";
  const searchSelector = "[data-global-phone-search]";
  const clearSelector = "[data-global-phone-clear]";
  const emptySelector = "[data-global-phone-empty]";

  const normalize = (value) => (value || "").toString().trim().toLowerCase().replace(/\s+/g, " ");

  const getDrawer = () => document.querySelector(selector);

  const getConfig = () => {
    const drawer = getDrawer();

    return {
      drawer,
      emptyLabel: drawer?.dataset.emptyLabel || "Choose phone",
      modelParamName: drawer?.dataset.modelParamName || "filter.v.option.model",
      knownParams: (drawer?.dataset.knownModelParamNames || "filter.v.option.model,filter.v.option.modell")
        .split(",")
        .map((param) => param.trim())
        .filter(Boolean),
      modelCollectionHandles: new Set(
        (drawer?.dataset.modelCollectionHandles || "mobilskal,slim-case,tough-case,magsafe-case,magnetic-case")
          .split(",")
          .map((handle) => handle.trim())
          .filter(Boolean)
      )
    };
  };

  const getCollectionHandle = (url) => {
    const match = url.pathname.match(/\/collections\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : "";
  };

  const getModelFromUrl = (url, knownParams) => {
    for (const paramName of knownParams) {
      const value = url.searchParams.get(paramName);

      if (value) {
        return value;
      }
    }

    return "";
  };

  const getStoredModel = () => {
    try {
      return window.localStorage.getItem(storageKey) || "";
    } catch (error) {
      return "";
    }
  };

  const storeModel = (model) => {
    try {
      window.localStorage.setItem(storageKey, model);
    } catch (error) {
      // Some browsers block storage in strict privacy contexts.
    }
  };

  const clearStoredModel = () => {
    try {
      window.localStorage.removeItem(storageKey);
    } catch (error) {
      // Some browsers block storage in strict privacy contexts.
    }
  };

  const getCurrentModel = () => {
    const config = getConfig();
    const urlModel = getModelFromUrl(new URL(window.location.href), config.knownParams);

    return urlModel || getStoredModel();
  };

  const buildUrlWithModel = (url, model, config, options = {}) => {
    const nextUrl = new URL(url.toString());

    config.knownParams.forEach((paramName) => nextUrl.searchParams.delete(paramName));

    if (model) {
      nextUrl.searchParams.set(config.modelParamName, model);
    }

    if (options.resetPage) {
      nextUrl.searchParams.delete("page");
    }

    return nextUrl;
  };

  const toSameOriginHref = (url) => {
    if (url.origin === window.location.origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }

    return url.toString();
  };

  const updateLabels = (model) => {
    const config = getConfig();

    document.querySelectorAll(labelSelector).forEach((label) => {
      label.textContent = model || config.emptyLabel;
    });
  };

  const updateOptions = (model) => {
    document.querySelectorAll(optionSelector).forEach((option) => {
      const isSelected = normalize(option.dataset.model) === normalize(model);
      option.setAttribute("aria-selected", isSelected ? "true" : "false");
    });
  };

  const resetSearch = () => {
    document.querySelectorAll(searchSelector).forEach((input) => {
      input.value = "";
    });

    document.querySelectorAll("[data-global-phone-group]").forEach((group) => {
      group.hidden = false;

      group.querySelectorAll(optionSelector).forEach((option) => {
        option.hidden = false;
      });
    });

    document.querySelectorAll(emptySelector).forEach((empty) => {
      empty.hidden = true;
    });
  };

  const updateControlledButtons = () => {
    const drawer = getDrawer();
    const isOpen = drawer?.hasAttribute("open");

    document.querySelectorAll(triggerSelector).forEach((trigger) => {
      trigger.setAttribute("aria-expanded", isOpen ? "true" : "false");
    });
  };

  const applyModelToCollectionLinks = (model) => {
    const config = getConfig();

    document.querySelectorAll("a[href]").forEach((link) => {
      const originalHref = link.dataset.globalPhoneOriginalHref || link.getAttribute("href");

      if (!originalHref || originalHref.startsWith("#") || originalHref.startsWith("mailto:") || originalHref.startsWith("tel:")) {
        return;
      }

      let url;

      try {
        url = new URL(originalHref, window.location.origin);
      } catch (error) {
        return;
      }

      if (url.origin !== window.location.origin || !config.modelCollectionHandles.has(getCollectionHandle(url))) {
        return;
      }

      link.dataset.globalPhoneOriginalHref = originalHref;
      link.setAttribute("href", toSameOriginHref(buildUrlWithModel(url, model, config)));
    });
  };

  const setSelectedModel = (model, options = {}) => {
    const config = getConfig();

    if (model) {
      storeModel(model);
    } else {
      clearStoredModel();
    }

    resetSearch();
    updateLabels(model);
    updateOptions(model);
    applyModelToCollectionLinks(model);
    updateControlledButtons();

    if (!options.navigateCollection) {
      return;
    }

    const currentUrl = new URL(window.location.href);

    if (!config.modelCollectionHandles.has(getCollectionHandle(currentUrl))) {
      config.drawer?.hide?.();
      return;
    }

    const nextUrl = buildUrlWithModel(currentUrl, model, config, { resetPage: true });

    if (nextUrl.toString() !== currentUrl.toString()) {
      window.location.href = nextUrl.toString();
    } else {
      config.drawer?.hide?.();
    }
  };

  const bindOptions = () => {
    document.querySelectorAll(optionSelector).forEach((option) => {
      if (option.dataset.globalPhoneBound === "true") {
        return;
      }

      option.dataset.globalPhoneBound = "true";
      option.addEventListener("click", () => {
        setSelectedModel(option.dataset.model, { navigateCollection: true });
      });
    });
  };

  const bindClear = () => {
    document.querySelectorAll(clearSelector).forEach((button) => {
      if (button.dataset.globalPhoneBound === "true") {
        return;
      }

      button.dataset.globalPhoneBound = "true";
      button.addEventListener("click", () => {
        setSelectedModel("", { navigateCollection: true });
      });
    });
  };

  const bindSearch = () => {
    document.querySelectorAll(searchSelector).forEach((input) => {
      if (input.dataset.globalPhoneBound === "true") {
        return;
      }

      input.dataset.globalPhoneBound = "true";
      input.addEventListener("input", () => {
        const query = normalize(input.value);
        let totalVisibleCount = 0;

        document.querySelectorAll("[data-global-phone-group]").forEach((group) => {
          let visibleCount = 0;

          group.querySelectorAll(optionSelector).forEach((option) => {
            const isVisible = normalize(option.dataset.model).includes(query);
            option.hidden = !isVisible;

            if (isVisible) {
              visibleCount += 1;
            }
          });

          group.hidden = visibleCount === 0;
          totalVisibleCount += visibleCount;
        });

        document.querySelectorAll(emptySelector).forEach((empty) => {
          empty.hidden = totalVisibleCount > 0;
        });
      });
    });
  };

  const init = () => {
    const model = getCurrentModel();

    bindOptions();
    bindClear();
    bindSearch();
    setSelectedModel(model);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
  document.addEventListener("dialog:after-show", updateControlledButtons);
  document.addEventListener("dialog:after-hide", updateControlledButtons);
})();
